import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { smartleadAPI } from '../lib/smartlead-api'

export interface MissingDomainInfo {
  domain: string
  mailbox_count: number
  mailboxes: string[]
}

export function useMissingDomains() {
  return useQuery({
    queryKey: ['missing-domains'],
    queryFn: async (): Promise<MissingDomainInfo[]> => {
      // Check for demo mode first
      if (!smartleadAPI.isConfigured()) {
        console.log('🔑 [useMissingDomains] Smartlead API not configured, returning demo missing domains...')
        
        // In demo mode, return empty array since all domains are properly configured
        const demoMissingDomains: MissingDomainInfo[] = []
        
        console.log('📊 Demo: No missing domains (all demo domains are properly configured)')
        return demoMissingDomains
      }
      
      console.log('Fetching missing domains...')
      
      // Get domains that have mailboxes but don't exist in domains table
      const { data: missingDomainsData, error } = await supabase
        .from('mailboxes')
        .select('domain, email')
        .not('domain', 'is', null)
        .neq('domain', '')

      if (error) {
        console.error('Error fetching mailboxes for missing domains check:', error)
        throw error
      }

      // Get all existing domains
      const { data: existingDomains, error: domainsError } = await supabase
        .from('domains')
        .select('domain')

      if (domainsError) {
        console.error('Error fetching existing domains:', domainsError)
        throw domainsError
      }

      const existingDomainSet = new Set(existingDomains?.map(d => d.domain) || [])
      
      // Group mailboxes by domain and filter out existing domains
      const domainGroups: { [key: string]: string[] } = {}
      
      missingDomainsData?.forEach(mailbox => {
        if (!existingDomainSet.has(mailbox.domain)) {
          if (!domainGroups[mailbox.domain]) {
            domainGroups[mailbox.domain] = []
          }
          domainGroups[mailbox.domain].push(mailbox.email)
        }
      })

      const result = Object.entries(domainGroups).map(([domain, mailboxes]) => ({
        domain,
        mailbox_count: mailboxes.length,
        mailboxes
      }))

      console.log('Missing domains found:', result)
      return result
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}